import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { absoluteImageUrl } from "@/components/ImageUploader";

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

const cssVar = (name, fallback) =>
  (getComputedStyle(document.documentElement).getPropertyValue(name) || "").trim() || fallback;

const roundRectPath = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const wrapText = (ctx, text, maxWidth, maxLines) => {
  const words = text.split(/\s+/);
  const lines = [];
  let line = "";
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
      if (lines.length === maxLines - 1) break;
    } else {
      line = test;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  if (lines.length === maxLines && words.join(" ") !== lines.join(" ") && ctx.measureText(lines[maxLines - 1]).width > maxWidth - 60) {
    lines[maxLines - 1] = `${lines[maxLines - 1].slice(0, -1).trimEnd()}…`;
  }
  return lines;
};

const loadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

const drawCover = (ctx, img, x, y, w, h) => {
  const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
  const sw = w / scale;
  const sh = h / scale;
  ctx.drawImage(img, (img.naturalWidth - sw) / 2, (img.naturalHeight - sh) / 2, sw, sh, x, y, w, h);
};

const paintBase = (ctx, W, H, accent) => {
  ctx.fillStyle = "#0D0907";
  ctx.fillRect(0, 0, W, H);
  // pată de cafea în colț + boabe de grain
  const g = ctx.createRadialGradient(W - 120, 260, 40, W - 120, 260, 520);
  g.addColorStop(0, "rgba(138,90,58,0.55)");
  g.addColorStop(1, "rgba(13,9,7,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, W, 14);
  ctx.fillRect(0, H - 14, W, 14);
};

const paintBrand = (ctx, W, accent) => {
  ctx.textAlign = "center";
  ctx.fillStyle = "#F7F1E8";
  ctx.font = '500 60px "Cormorant Garamond", Georgia, serif';
  ctx.fillText("La Cafea,", W / 2, 190);
  ctx.fillStyle = accent;
  ctx.font = '700 30px "Outfit", sans-serif';
  ctx.fillText("F Ă R Ă   M E N A J A M E N T E", W / 2, 245);
};

const buildStoryCanvas = async ({ title, cover, punchline, variant }) => {
  const W = 1080;
  const H = 1920;
  const accent = cssVar("--accent", "#E81B33");
  try {
    await Promise.all([document.fonts.load('500 76px "Cormorant Garamond"'), document.fonts.load('600 30px "Outfit"')]);
  } catch {
    // fonturile de sistem preiau rolul
  }
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  paintBase(ctx, W, H, accent);
  paintBrand(ctx, W, accent);

  if (variant === "quote") {
    // Citatul: replica de final, mare, cu roșu, cu ghilimele uriașe
    ctx.textAlign = "center";
    ctx.fillStyle = "rgba(232,27,51,0.28)";
    ctx.font = '700 420px "Cormorant Garamond", Georgia, serif';
    ctx.fillText("„", W / 2 - 330, 760);
    ctx.fillStyle = accent;
    ctx.font = '600 86px "Cormorant Garamond", Georgia, serif';
    const qLines = wrapText(ctx, punchline, 900, 6);
    const qTop = H / 2 - (qLines.length * 104) / 2 + 40;
    qLines.forEach((l, i) => ctx.fillText(l, W / 2, qTop + i * 104));
    const after = qTop + qLines.length * 104 + 10;
    ctx.fillStyle = "#F7F1E8";
    ctx.fillRect(W / 2 - 70, after, 140, 4);
  } else {
    const img = cover ? await loadImage(absoluteImageUrl(cover)) : null;
    const cardW = 880;
    const cardH = img ? 980 : 0;
    const cardX = (W - cardW) / 2;
    const cardY = 340;
    if (img) {
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.6)";
      ctx.shadowBlur = 60;
      ctx.shadowOffsetY = 24;
      ctx.fillStyle = "#2B1B13";
      roundRectPath(ctx, cardX, cardY, cardW, cardH, 28);
      ctx.fill();
      ctx.shadowColor = "transparent";
      ctx.strokeStyle = accent;
      ctx.lineWidth = 6;
      ctx.stroke();
      roundRectPath(ctx, cardX + 24, cardY + 24, cardW - 48, cardH - 48, 20);
      ctx.save();
      ctx.clip();
      drawCover(ctx, img, cardX + 24, cardY + 24, cardW - 48, cardH - 48);
      ctx.restore();
      ctx.restore();
    }
    ctx.textAlign = "center";
    ctx.fillStyle = "#F7F1E8";
    ctx.font = '500 74px "Cormorant Garamond", Georgia, serif';
    const titleTop = img ? cardY + cardH + 130 : 760;
    const lines = wrapText(ctx, title, 900, img ? 3 : 5);
    lines.forEach((l, i) => ctx.fillText(l, W / 2, titleTop + i * 92));
    const afterTitle = titleTop + lines.length * 92 + 10;
    ctx.fillStyle = accent;
    ctx.fillRect(W / 2 - 60, afterTitle, 120, 4);
  }

  ctx.textAlign = "center";
  ctx.fillStyle = accent;
  ctx.font = '700 34px "Outfit", sans-serif';
  ctx.fillText("la-cafea-fara-menajamente.ro", W / 2, H - 110);
  return canvas;
};

export const InstagramStoryButton = ({ slug, title, cover, punchline, variant = "post" }) => {
  const [busy, setBusy] = useState(false);
  const fileName = `${variant === "quote" ? "citat" : "story"}-${(slug || "post")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "post"}.png`;

  const generate = async () => {
    setBusy(true);
    try {
      const canvas = await buildStoryCanvas({ title, cover, punchline, variant });
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      if (!blob) throw new Error("canvas");
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title });
          return;
        } catch (err) {
          if (err && err.name === "AbortError") return;
        }
      }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      toast.success("Imaginea pentru story a fost descărcată — adaug-o pe Instagram din galeria telefonului.", {
        duration: 8000,
      });
    } catch {
      toast.error("Nu am putut pregăti imaginea acum. Mai încearcă o dată.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={generate}
      disabled={busy}
      data-testid={variant === "quote" ? "share-instagram-quote-btn" : "share-instagram-story-btn"}
      className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-60 ${variant === "quote" ? "bg-[var(--accent)]" : "bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]"}`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <InstagramIcon />} {variant === "quote" ? "Citatul pentru story" : "Story Instagram"}
    </button>
  );
};

export default InstagramStoryButton;
